import {
  Box,
  Typography,
  Stack,
  debounce,
  StackProps,
  Switch
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import LoaderComponent from "components/Loader";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import WidgetWraper from "components/Common/WidgetWraper";
import { InfoTooltip } from '../InfoTooltip';
import { buildBarChartOptions } from "../chartWidgetUtility";
import HighlightedText from "components/GlobalSearch/HighlightText";

interface CustomBarChartProps {
  title: any;
  isLoading?: boolean;
  data: any[];
  containerProps?: StackProps;
  isHappySignal?: boolean;
  showBottom10?: boolean;
  setShowBottom10?: any;
  isCost?: boolean;

  tooltipLabel?:
    | string
    | JSX.Element
    | { title: string; description: string | React.ReactNode }[];
  hasTooltip?: boolean;
  orientation?: "vertical" | "horizontal";
  slaTarget?: number;
  slaLabel?: string;
  colorScheme?: { above: string; below: string };
  barColor? : string;
  showGridLines?: boolean;
  tooltipFormatter?: (params: any) => string;
  onBarClick?: (params: any) => void;
}

const CustomBarChartWidget = ({
  title,
  isLoading,
  data,
  containerProps,
  isHappySignal = false,
  showBottom10,
  setShowBottom10,
  isCost = false,
  tooltipLabel,
  hasTooltip = true,
  orientation = "vertical",
  slaTarget,
  slaLabel = "SLA",
  colorScheme,
  barColor,
  showGridLines = false,
  tooltipFormatter,
  onBarClick
}: Readonly<CustomBarChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // -----------------------------
  // Default "No Data" options
  // -----------------------------
  const defaultOptions = {
    graphic: [
      {
        type: "rect",
        left: "center",
        top: "center",
        shape: { width: 300, height: 200 },
        style: {
          fill: colors.widget.background.card
        }
      }
    ],
    title: {
      show: true,
      text: "No data",
      textStyle: {
        color: colors.widget.text.main,
        fontSize: 16,
        fontWeight: "normal"
      },
      left: "center",
      top: "center"
    },
    backgroundColor: colors.widget.background.card
  };

  const dynamicOptions = useMemo(() => {
  const result = buildBarChartOptions({
    data,
    orientation,
    showGridLines,
    colors,
    colorScheme,
    barColor,
    slaTarget,
    slaLabel,
    tooltipFormatter
  });

  return result || defaultOptions;
}, [
  data,
  orientation,
  showGridLines,
  colors,
  colorScheme,
  barColor,
  slaTarget,
  slaLabel,
  tooltipFormatter
]);

  const finalOptions = data?.length ? dynamicOptions : defaultOptions;

  // -----------------------------
  // Resize listener
  // -----------------------------
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartContainerRef.current) {
        setWindowSize(window.innerWidth);
      }
    }, 200);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <WidgetWraper>
      <Box
        sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
        {...containerProps}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: isHappySignal ? 4 : 0 }}
        >
          <Stack direction="row" alignItems="center" sx={{ marginBottom: 3 }}>
            <Typography
              variant="widgetTitleNew"
              sx={{ color: colors.widget.text.title }}
            >
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && !!tooltipLabel && (
              <InfoTooltip label={tooltipLabel} />
            )}
          </Stack>

          {/* Top 10 / Bottom 10 toggle */}
          {setShowBottom10 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="bodyText" fontWeight={!showBottom10 ? 500 : 400}>
                Top 10
              </Typography>
              <Switch
                color={showBottom10 ? "info" : "warning"}
                onChange={(e) => setShowBottom10(e.target.checked)}
                checked={showBottom10}
                size="small"
              />
              <Typography variant="bodyText" fontWeight={showBottom10 ? 500 : 400}>
                Bottom 10
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* CHART */}
        <Box
          sx={{ width: "100%", height: isCost ? 319 : 350 }}
          key={windowSize}
          ref={chartContainerRef}
        >
          {!isLoading ? (
            <ReactECharts
              option={finalOptions}
              style={{ width: "100%", height: "100%" }}
              onEvents={{
                click: (params: any) => onBarClick?.(params)
              }}
            />
          ) : (
            <LoaderComponent pt={20} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default CustomBarChartWidget;
